import React from 'react'
import { RadioGroup, RadioGroupItem, type RadioGroupItemProps } from '../../components/radio-group'

// Base UIRadioGroup exports - for other composed components
export const UIRadioGroupBase = RadioGroup
export const UIRadioGroupItemBase = RadioGroupItem

import { UILabel } from './ui-label'
import { UITypography } from '../display/ui-typography'
import { cn } from '../../lib/utils'
import { UIBox } from '../layout/ui-box'

export interface UIRadioGroupOption {
  value: string
  label: string
  id?: string
  disabled?: boolean
  description?: string
  icon?: React.ReactNode
}

export interface UIRadioGroupProps extends React.ComponentProps<typeof RadioGroup> {
  options: UIRadioGroupOption[]
  label?: string
  description?: string
  /**
   * 라디오 그룹 방향
   */
  orientation?: 'vertical' | 'horizontal'
  /**
   * 라디오 그룹 크기
   */
  size?: 'sm' | 'default' | 'lg'
  /**
   * 색상 변형
   */
  variant?: 'default' | 'accent' | 'success' | 'warning' | 'destructive'
  /**
   * 오류 메시지
   */
  error?: string
  /**
   * 필수 여부
   */
  required?: boolean
}

const sizeVariants = {
  sm: {
    label: 'text-xs font-medium leading-none',
    description: 'text-xs text-muted-foreground',
    container: 'gap-1.5'
  },
  default: {
    label: 'text-sm font-medium leading-none',
    description: 'text-xs text-muted-foreground',
    container: 'gap-2'
  },
  lg: {
    label: 'text-base font-medium leading-none',
    description: 'text-sm text-muted-foreground',
    container: 'gap-3'
  }
}

const variantStyles = {
  default: 'border-muted-foreground data-[state=checked]:border-primary data-[state=checked]:text-primary',
  accent: 'border-muted-foreground data-[state=checked]:border-accent data-[state=checked]:text-accent',
  success: 'border-muted-foreground data-[state=checked]:border-green-500 data-[state=checked]:text-green-500',
  warning: 'border-muted-foreground data-[state=checked]:border-yellow-500 data-[state=checked]:text-yellow-500',
  destructive: 'border-muted-foreground data-[state=checked]:border-destructive data-[state=checked]:text-destructive'
}

/**
 * 라디오 그룹 컴포넌트
 * options 배열을 통해 쉽게 라디오 버튼 그룹을 생성할 수 있습니다.
 * 다양한 크기, 색상, 방향을 지원합니다.
 */
export const UIRadioGroup = React.forwardRef<
  React.ComponentRef<typeof RadioGroup>,
  UIRadioGroupProps
>(({
  className,
  options,
  label,
  description,
  orientation = 'vertical',
  size = 'default',
  variant = 'default',
  error,
  required = false,
  value,
  defaultValue,
  onValueChange,
  ...props
}, ref) => {
  const sizeClass = sizeVariants[size]
  const variantClass = variantStyles[variant]
  const isControlled = value !== undefined
  const [internalValue, setInternalValue] = React.useState(defaultValue || '')
  const currentValue = isControlled ? value : internalValue

  const handleValueChange = (newValue: string) => {
    if (!isControlled) {
      setInternalValue(newValue)
    }
    onValueChange?.(newValue)
  }

  return (
    <UIBox className="space-y-3">
      {label && (
        <UILabel className={cn(
          "font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
          size === 'sm' ? 'text-sm' : size === 'lg' ? 'text-lg' : 'text-base',
          required && "after:content-['*'] after:ml-0.5 after:text-destructive",
          error && "text-destructive"
        )}>
          {label}
        </UILabel>
      )}
      {description && (
        <UITypography
          variant={size === 'sm' ? 'caption' : size === 'lg' ? 'body1' : 'caption'}
          color="muted"
        >
          {description}
        </UITypography>
      )}

      <RadioGroup
        ref={ref}
        className={cn(
          orientation === 'horizontal' ? 'flex flex-wrap gap-6' : 'space-y-3',
          className
        )}
        value={currentValue}
        onValueChange={handleValueChange}
        {...props}
      >
        {options.map((option, index) => {
          const itemId = option.id || `${props.name || 'radio'}-${option.value}-${index}`

          return (
            <UIBox key={option.value} className={cn(
              "flex",
              option.description ? "items-start" : "items-center",
              sizeClass.container,
              orientation === 'horizontal' && "items-center"
            )}>
              <RadioGroupItem
                value={option.value}
                id={itemId}
                disabled={option.disabled}
                size={size}
                className={cn(
                  "border-2",
                  variantClass,
                  error && "border-destructive focus-visible:ring-destructive/20"
                )}
              />
              <UIBox className="flex-1 space-y-1">
                <UILabel
                  htmlFor={itemId}
                  className={cn(
                    "cursor-pointer font-medium",
                    sizeClass.label,
                    option.disabled && "opacity-50 cursor-not-allowed",
                    error && "text-destructive"
                  )}
                >
                  {option.icon && (
                    <UITypography as="span" variant="body1" className="mr-2 inline-flex">{option.icon}</UITypography>
                  )}
                  {option.label}
                </UILabel>
                {option.description && (
                  <UITypography
                    variant="caption"
                    color="muted"
                  >
                    {option.description}
                  </UITypography>
                )}
              </UIBox>
            </UIBox>
          )
        })}
      </RadioGroup>

      {error && (
        <UITypography
          variant={size === 'sm' ? 'caption' : size === 'lg' ? 'body1' : 'body2'}
          color="destructive"
          className="animate-in slide-in-from-top-1 duration-200"
          role="alert"
        >
          {error}
        </UITypography>
      )}
    </UIBox>
  )
})

UIRadioGroup.displayName = "UIRadioGroup"